import { useEffect } from 'react';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import PropTypes from 'prop-types';
import { routesPropTypes } from './routerPropTypes';
import { Login } from '../routes/login';
import { Dashboard } from '../routes/dashboards/Dashboard';
import { Exception } from '../components';
import { useAuthContext } from '../contexts/AuthContext';
import { ProfileRouter } from '../routes/profile/ProfileRouter';
import { Confirmation } from '../routes/emailConfirmation/Confirmation';
import { Settings } from '../routes/settings/settings';
import { UserRouter } from '../routes/users/UserRouter';
import { OwnerRouter } from '../routes/owners/OwnerRouter';
import { PensionerRouter } from '../routes/pensioners/PensionerRouter';
import { SupervisionRouter } from '../routes/supervisions/SupervisionRouter';
import { ReservationRouter } from '../routes/reservation/ReservationRouter';
import { SupervisionPriceRouter } from '../routes/supervision-prices/SupervisionPriceRouter';
import { InvoiceRouter } from '../routes/invoices/InvoiceRouter';
import { TemplateDocRouter } from '../routes/template-docs/TemplateDocsRouter';
import { AdminTheme } from '../assets/styles/Theme/AdminTheme';
import { RequireAuth } from './RequireAuth';
/**
 * BaseRouter component.
 * @component
 *
 * Shared router component that handles common routing logic for admin and developer routers.
 * @param {Object} props - Component props.
 * @param {Object} props.routes - Routes configuration object.
 * @param {Object} props.outOfNavRoutes - Out of navigation routes configuration object.
 * @param {boolean} props.useIndexForDashboard - Whether to use index route for dashboard.
 * @param {Array<ReactElement>} props.additionalRoutes - Additional route elements specific to the router.
 * @returns {ReactElement} The BaseRouter component.
 */
export const BaseRouter = ({
  routes,
  outOfNavRoutes,
  useIndexForDashboard = false,
  additionalRoutes = []
}) => {
  const { isValid } = useAuthContext();

  useEffect(() => {
    const body = document.body;
    if (body) {
      body.className = 'admin';
    }
  });

  return (
    <BrowserRouter>
      <AdminTheme />
      <Routes>
        <Route path={outOfNavRoutes.LOGIN} element={<Login />} />
        <Route
          path={`${outOfNavRoutes.CONFIRMATION}/:token`}
          element={<Confirmation />}
        />
        <Route element={<RequireAuth isValid={isValid} />}>
          <Route
            path={`${outOfNavRoutes.PROFILE}/*`}
            element={<ProfileRouter />}
          />
          <Route path={outOfNavRoutes.SETTINGS} element={<Settings />} />
          {useIndexForDashboard ? (
            <Route index element={<Dashboard />} />
          ) : (
            <Route path={routes.DASHBOARD} element={<Dashboard />} />
          )}
          <Route path={`${routes.USERS}/*`} element={<UserRouter />} />
          <Route path={`${routes.OWNERS}/*`} element={<OwnerRouter />} />
          <Route
            path={`${routes.PENSIONERS}/*`}
            element={<PensionerRouter />}
          />
          <Route
            path={`${routes.SUPERVISIONS}/*`}
            element={<SupervisionRouter />}
          />
          <Route
            path={`${routes.RESERVATIONS}/*`}
            element={<ReservationRouter />}
          />
          <Route
            path={`${routes.SUPERVISION_PRICES}/*`}
            element={<SupervisionPriceRouter />}
          />
          <Route path={`${routes.INVOICES}/*`} element={<InvoiceRouter />} />
          <Route
            path={`${routes.TEMPLATE_DOCS}/*`}
            element={<TemplateDocRouter />}
          />
          {additionalRoutes}
          <Route path="*" element={<Exception />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
};

BaseRouter.propTypes = {
  routes: routesPropTypes.isRequired,
  outOfNavRoutes: PropTypes.shape({
    LOGIN: PropTypes.string.isRequired,
    CONFIRMATION: PropTypes.string.isRequired,
    PROFILE: PropTypes.string.isRequired,
    SETTINGS: PropTypes.string.isRequired
  }).isRequired,
  useIndexForDashboard: PropTypes.bool.isRequired,
  additionalRoutes: PropTypes.arrayOf(PropTypes.element).isRequired
};
